"use client";

import { useEffect, useRef, useState } from "react";
import CtaButton from "../ui/buttons/CtaButton";

declare global {
  interface Window {
    /** Trustindex's own teardown: destroys every live widget instance. */
    Trustindex?: { destroyWidgets?: () => void };
    /** Scans the document for uninitialised `.trustindex-widget` containers. */
    renderTrustindexWidgets?: () => void;
  }
}

const LOADER_SRC =
  "https://cdn.trustindex.io/loader.js?9a994f75558e59392906b5b2849";

const GoogleReviews = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  // Ensure this component only renders after hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !sectionRef.current) return;

    /*
     * Load the loader once per document, not once per mount.
     *
     * This section sits on five routes, so moving between any two of them used
     * to unmount it and mount it again — appending the 95 kB loader a second
     * time. Re-running it builds a second widget instance and pushes it onto
     * window.tiWidgetInstances, which the loader's own document-level touch,
     * click and resize handlers then iterate in full. An instance left over
     * from the previous page still points at the DOM React detached during the
     * navigation, so its carousel indexes past the end of a container that no
     * longer holds any reviews and dies on `.cloneNode` of undefined — a throw
     * from inside their listener, on a stack with no frame of ours.
     *
     * Nothing has to replace the second load: the loader installs a
     * MutationObserver on <body> and re-scans on every DOM change, and it skips
     * containers already carrying data-trustindex-widget, so the section below
     * is picked up on its own whichever route mounts it. The explicit render
     * call is only so a remount doesn't have to wait for a mutation to be
     * noticed.
     *
     * The script goes on <head> deliberately. Inside the section, React would
     * take it away on unmount, the check below would miss it next time, and the
     * double load would be back.
     */
    if (document.querySelector(`script[src="${LOADER_SRC}"]`)) {
      window.renderTrustindexWidgets?.();
    } else {
      const script = document.createElement("script");
      script.src = LOADER_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => {
      // Hand the instances back before React takes their DOM away, so none of
      // them survives the navigation holding a detached container. Every route
      // renders exactly one of these sections, so "all widgets" and "this
      // component's widgets" are the same set — keep it that way, or this
      // cleanup will tear down a sibling's widget too.
      window.Trustindex?.destroyWidgets?.();
    };
  }, [mounted]);

  if (!mounted) return null;

  return (
    <section
      id='reviews-section'
      ref={sectionRef}
      className='relative px-2 max-w-325 w-full text-left py-5'
    >
      <div className='mb-6 text-center'>
        <CtaButton
          text='Napísať recenziu'
          href='https://g.page/r/CQrnE9dA6fyPEBM/review'
          targetBlank
        />
      </div>

      <div
        className='trustindex-widget'
        data-ti-widget='google'
        data-ti-id='9a994f75558e59392906b5b2849'
      />
    </section>
  );
};

export default GoogleReviews;
